import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import '@maersk-global/mds-components-core/mc-input';
import { AuthHelper } from '../../auth/auth-helper';
import { Roles } from '../../auth/Roles';
import { EMPTY, Subject, catchError, forkJoin, map, switchMap, takeUntil } from 'rxjs';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-switch';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-button';
import { CategoryCardInput, ScorecardPeriod } from '../../models/vsc-scorecard.model';
import { VscCategoryCardComponent } from '../../shared-components/vsc-category-card/vsc-category-card.component';
import { OverallPerformanceCardComponent } from './overall-performance-card.component';
import { CategoryPerformanceCardComponent } from './category-performance-card.component';
import { VscKpiCategoryService } from '../../services/vsc-kpi-category.service';
import {
  ScorecardScoreData,
  ScorecardStaticData,
  WeeklyScoreData,
  buildScorecardPeriods,
  buildWeeklyScorecardPeriods,
  mapToCategories,
  mapToWeeklyCategories,
  parseMonthLabel,
} from './vsc-scorecard.mapper';

type PeriodType = 'weekly' | 'monthly';

/**
 * Time allowed between expanding every card and snapshotting the charts. Covers two
 * queued task hops (category expand -> KPI expand -> chart build) plus Chart.js's ~1s
 * entry animation; snapshotting earlier captures half-drawn or empty canvases.
 */
const CHART_SETTLE_MS = 1500;

/** Extra grace given to any canvas still blank after the initial settle. */
const CHART_RETRY_MS = 500;
const CHART_RETRY_ATTEMPTS = 4;

/** Upper bound on waiting for the snapshot images to be painted before printing. */
const DECODE_TIMEOUT_MS = 3000;

/** Earliest month offered in the month filter. */
const FIRST_MONTH = new Date(2025, 0, 1);

@Component({
  selector: 'app-vsc-detailed-view',
  imports: [CommonModule, VscCategoryCardComponent, OverallPerformanceCardComponent, CategoryPerformanceCardComponent],
  templateUrl: './vsc-detailed-view.component.html',
  styleUrl: './vsc-detailed-view.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VscDetailedViewComponent implements OnInit, OnDestroy {
  private readonly service = inject(VscKpiCategoryService);
  private readonly router = inject(Router);
  private readonly hostRef: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChildren(VscCategoryCardComponent) private readonly categoryCards!: QueryList<VscCategoryCardComponent>;
  @ViewChild('periodContainer') private readonly periodContainer!: ElementRef;
  // Optional: the summary card is not rendered while loading or on an API error.
  @ViewChild(CategoryPerformanceCardComponent) private readonly categoryPerformanceCard?: CategoryPerformanceCardComponent;

  readonly isShopUser = AuthHelper.user?.roles.some((r) => r.toLowerCase() === Roles.SHOP) ?? false;
  private readonly assignedShopCodes = new Set(AuthHelper.user?.maintenanceShops?.map((s) => s.maintenanceShopCode.toLowerCase()) ?? []);

  readonly vendorCode = signal('');
  readonly shopCodeInput = signal('');
  readonly shopCodeError = signal<string | null>(null);
  readonly isShopView = signal(this.isShopUser);
  readonly periodType = signal<PeriodType>('monthly');
  /** Weekly selection is always within the current calendar year. */
  readonly weekYear = new Date().getFullYear();
  private readonly monthOffset = new Date().getDate() <= 12 ? 2 : 1;
  readonly selectedMonth = signal(this.formatMonth(new Date(new Date().getFullYear(), new Date().getMonth() - this.monthOffset, 1)));
  readonly selectedWeek = signal(this.maxWeekForLastMonth());
  readonly periodDropdownOpen = signal(false);
  readonly hasAnyExpanded = signal(false);
  readonly compiledAt = signal<string | null>(null);
  readonly isLoadingCategories = signal(false);
  readonly categoriesError = signal<string | null>(null);

  readonly isPreparingDownload = signal(false);
  /** Stamped into the print-only header just before the print dialog opens. */
  readonly printGeneratedAt = signal('');
  /** Both overridden in tests so the suite does not sit out real wall-clock waits. */
  private readonly chartSettleMs = CHART_SETTLE_MS;
  private readonly decodeTimeoutMs = DECODE_TIMEOUT_MS;

  readonly categories = signal<CategoryCardInput[]>([]);

  readonly visibleCategories = computed(() => this.categories().filter((c) => c.categoryWeight > 0));

  readonly displayCategories = computed(() => this.visibleCategories().map((c) => ({ ...c, isShopView: this.isShopView() })));

  readonly periodLabel = computed(() =>
    this.periodType() === 'weekly' ? `Weekly: ${this.weekYear}_WK${this.selectedWeek()}` : `Monthly: ${this.selectedMonth()}`,
  );

  // Month label that drives OverallPerformanceCard (FinalScore) and the template.
  // In weekly mode, derives the month from the selected week so FinalScore re-fetches correctly.
  readonly effectiveMonthLabel = computed(() => {
    if (this.periodType() !== 'weekly') return this.selectedMonth();
    const week = this.selectedWeek();
    const year = new Date().getFullYear();
    const jan4 = new Date(year, 0, 4);
    const dayInWeek = new Date(jan4.getTime() + (week - 1) * 7 * 24 * 60 * 60 * 1000);
    return this.formatMonth(dayInWeek);
  });

  /** Newest month first. */
  readonly monthOptions: string[] = this.buildMonthOptions();
  /** Newest (highest) week first. */
  readonly weekOptions: number[] = Array.from({ length: this.maxWeekForLastMonth() }, (_, i) => this.maxWeekForLastMonth() - i);

  private readonly loadTrigger$ = new Subject<
    | { type: 'monthly'; vendorCode: string; month: string; year: number }
    | { type: 'weekly'; vendorCode: string; week: number; year: number }
  >();
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly route: ActivatedRoute) {
    this.loadTrigger$
      .pipe(
        switchMap((trigger) => {
          this.isLoadingCategories.set(true);
          this.categoriesError.set(null);
          this.categories.set([]);

          const baseRequests = {
            settings: this.service.getVendorSettingsByCode(trigger.vendorCode),
            master: this.service.getKpiMaster(),
            cats: this.service.getKpiCategories(),
          };

          const obs$ =
            trigger.type === 'weekly'
              ? forkJoin({
                  ...baseRequests,
                  catScores: this.service.postKpiCategoryScore({
                    vendorCode: trigger.vendorCode,
                    period: [this.weekToMonthPeriod(trigger.week, trigger.year)],
                  }),
                  weeklyScores: this.service.postKpiScoreWeekly({
                    vendorCode: trigger.vendorCode,
                    period: buildWeeklyScorecardPeriods(trigger.week, trigger.year),
                  }),
                  kpiScores: this.service.postKpiScore({
                    vendorCode: trigger.vendorCode,
                    period: buildScorecardPeriods(this.weekToMonthPeriod(trigger.week, trigger.year).month, trigger.year),
                  }),
                }).pipe(
                  map((r) => {
                    const sd: ScorecardStaticData = { vendorSettings: r.settings, kpiMaster: r.master, kpiCategories: r.cats };
                    const wd: WeeklyScoreData = {
                      categoryScores: r.catScores.kpiData,
                      weeklyKpiScores: r.weeklyScores.kpiData,
                      monthlyKpiScores: r.kpiScores.kpiData,
                    };
                    return mapToWeeklyCategories(sd, wd, trigger.week, trigger.year);
                  }),
                )
              : forkJoin({
                  ...baseRequests,
                  catScores: this.service.postKpiCategoryScore({
                    vendorCode: trigger.vendorCode,
                    period: [{ month: trigger.month, year: trigger.year }],
                  }),
                  kpiScores: this.service.postKpiScore({
                    vendorCode: trigger.vendorCode,
                    period: buildScorecardPeriods(trigger.month, trigger.year),
                  }),
                }).pipe(
                  map((r) => {
                    const sd: ScorecardStaticData = { vendorSettings: r.settings, kpiMaster: r.master, kpiCategories: r.cats };
                    const sc: ScorecardScoreData = { categoryScores: r.catScores.kpiData, kpiScores: r.kpiScores.kpiData };
                    return mapToCategories(sd, sc, this.selectedMonth());
                  }),
                );

          return obs$.pipe(
            catchError((err: unknown) => {
              const body = (err as { error?: unknown })?.error;
              const msg =
                typeof body === 'string' && body.trim()
                  ? body.trim()
                  : `Failed to load scorecard for ${trigger.vendorCode}. Please try again.`;
              this.categoriesError.set(msg);
              this.isLoadingCategories.set(false);
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((categories: CategoryCardInput[]) => {
        this.categories.set(categories);
        this.isLoadingCategories.set(false);
      });

    effect(() => {
      const code = this.vendorCode();
      if (!code) return;
      if (this.periodType() === 'weekly') {
        this.loadTrigger$.next({ type: 'weekly', vendorCode: code, week: this.selectedWeek(), year: new Date().getFullYear() });
      } else {
        const { month, year } = parseMonthLabel(this.selectedMonth());
        this.loadTrigger$.next({ type: 'monthly', vendorCode: code, month, year });
      }
    });
  }

  ngOnInit(): void {
    // Month first: the load effect reads selectedMonth(), so applying the list-view
    // selection before vendorCode() means only one fetch, already for the right period.
    const month = this.route.snapshot.queryParamMap.get('month');
    if (month && this.monthOptions.includes(month)) {
      this.selectedMonth.set(month);
    }

    const code = this.route.snapshot.paramMap.get('vendorCode') ?? '';
    this.vendorCode.set(code);
    this.shopCodeInput.set(code);

    // `month` is only a handover from the list view — once applied it lives in
    // selectedMonth(), so drop it from the address bar. replaceUrl keeps Back
    // pointing at wherever the user came from instead of this same page.
    if (month) {
      void this.router.navigate(['/scorecard', code], { replaceUrl: true });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.periodDropdownOpen() && !this.periodContainer?.nativeElement.contains(event.target)) {
      this.periodDropdownOpen.set(false);
    }
  }

  onShopViewToggle(event: Event): void {
    this.isShopView.set(!!(event as CustomEvent).detail);
  }

  onShopCodeInput(event: Event): void {
    this.shopCodeInput.set((event.target as HTMLInputElement).value ?? '');
    this.shopCodeError.set(null);
  }

  onShopCodeKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    const code = this.shopCodeInput().trim().toUpperCase();
    if (!code) return;
    if (!this.assignedShopCodes.has(code.toLowerCase())) {
      this.shopCodeError.set('Shop code not assigned to the user');
      return;
    }
    this.shopCodeError.set(null);
    this.vendorCode.set(code);
    // No month in the URL: this route is reused rather than recreated, so
    // selectedMonth() survives and the new shop loads for the same period.
    this.router.navigate(['/scorecard', code]);
  }

  togglePeriodDropdown(): void {
    this.periodDropdownOpen.update((v) => !v);
  }

  selectPeriodType(type: PeriodType): void {
    this.periodType.set(type);
  }

  selectMonth(month: string): void {
    this.selectedMonth.set(month);
    this.periodDropdownOpen.set(false);
  }

  selectWeek(week: number): void {
    this.selectedWeek.set(week);
    this.periodDropdownOpen.set(false);
  }

  expandAll(): void {
    this.categoryCards.forEach((c) => c.isExpanded.set(true));
    this.categoryPerformanceCard?.expandAll();
    this.hasAnyExpanded.set(true);
    setTimeout(() => this.categoryCards.forEach((c) => c.expandAllKpis()), 0);
  }

  collapseAll(): void {
    this.categoryCards.forEach((c) => c.isExpanded.set(false));
    this.categoryPerformanceCard?.collapseAll();
    this.hasAnyExpanded.set(false);
  }

  onCompiledAt(iso: string): void {
    this.compiledAt.set(iso);
  }

  formatCompiledAt(iso: string): string {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${date}, ${time}`;
  }

  /**
   * Expands the whole scorecard, freezes the charts, then hands the live page to the
   * browser print dialog (same window.print() route as the work order printout) and
   * puts the user's own expand/collapse state back afterwards.
   */
  async onDownloadClicked(): Promise<void> {
    if (this.isPreparingDownload()) return;

    this.periodDropdownOpen.set(false);
    this.isPreparingDownload.set(true);
    this.printGeneratedAt.set(this.formatCompiledAt(new Date().toISOString()));

    const restoreExpansion = this.captureExpansionState();

    // The export is always fully expanded, whatever the user last clicked.
    this.expandAll();
    await this.waitForChartsToRender();

    // Chart.js redraws on the width change that print layout causes, which blanks the
    // canvas mid-print. Printing a snapshot <img> instead sidesteps that entirely.
    const chartSnapshots = this.snapshotCharts();
    // A data: URL still decodes asynchronously. Printing before the decode lands is
    // what left the first few charts as empty boxes in the PDF.
    await this.waitForImagesToDecode(chartSnapshots);

    const pageRule = this.addLandscapePageRule();
    // Browsers default the saved PDF's name to document.title.
    const originalTitle = document.title;
    document.title = `${originalTitle} [${this.vendorCode()}]`;

    const cleanUp = (): void => {
      pageRule.remove();
      document.title = originalTitle;
      chartSnapshots.forEach((img) => img.remove());
      restoreExpansion();
      this.isPreparingDownload.set(false);
    };

    globalThis.addEventListener('afterprint', cleanUp, { once: true });
    (globalThis as unknown as Window).print();
  }

  /** Snapshots every card's expanded state and returns the undo for it. */
  private captureExpansionState(): () => void {
    const snapshot = this.categoryCards.map((card) => ({
      card,
      expanded: card.isExpanded(),
      kpis: card.kpiExpansionState(),
    }));
    const hadAnyExpanded = this.hasAnyExpanded();
    const summaryCard = this.categoryPerformanceCard;
    const summaryRows = summaryCard?.expansionState();

    return () => {
      snapshot.forEach((s) => {
        s.card.isExpanded.set(s.expanded);
        s.card.restoreKpiExpansion(s.kpis);
      });
      if (summaryCard && summaryRows) summaryCard.restoreExpansion(summaryRows);
      this.hasAnyExpanded.set(hadAnyExpanded);
    };
  }

  /**
   * Waits out the entry animation, then keeps waiting while any canvas is still blank.
   * A resize triggered by the expand restarts Chart.js's animation, so a single fixed
   * delay is not enough to guarantee every chart has actually drawn.
   */
  private async waitForChartsToRender(): Promise<void> {
    await this.delay(this.chartSettleMs);

    for (let attempt = 0; attempt < CHART_RETRY_ATTEMPTS; attempt++) {
      if (!this.chartCanvases().some((c) => this.isCanvasBlank(c))) return;
      await this.delay(CHART_RETRY_MS);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private chartCanvases(): HTMLCanvasElement[] {
    return Array.from(this.hostRef.nativeElement.querySelectorAll<HTMLCanvasElement>('canvas'));
  }

  /** True when every pixel is still fully transparent, i.e. Chart.js has not drawn yet. */
  private isCanvasBlank(canvas: HTMLCanvasElement): boolean {
    if (!canvas.width || !canvas.height) return true;
    try {
      const { data } = canvas.getContext('2d')!.getImageData(0, 0, canvas.width, canvas.height);
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] !== 0) return false;
      }
      return true;
    } catch {
      return false; // unreadable (tainted or no 2d context) — don't stall on it
    }
  }

  /** Inserts a PNG of each chart canvas next to it; print CSS swaps canvas for image. */
  private snapshotCharts(): HTMLImageElement[] {
    return this.chartCanvases().reduce<HTMLImageElement[]>((images, canvas) => {
      let dataUrl: string;
      try {
        dataUrl = canvas.toDataURL('image/png');
      } catch {
        return images; // tainted canvas — fall back to printing nothing for this chart
      }
      const img = document.createElement('img');
      img.className = 'vsc-print-chart';
      img.alt = '';
      img.src = dataUrl;
      canvas.after(img);
      images.push(img);
      return images;
    }, []);
  }

  /**
   * Resolves once every snapshot is painted, or after DECODE_TIMEOUT_MS — a decode that
   * never settles must not leave the user stuck on a spinner with no print dialog.
   */
  private waitForImagesToDecode(images: HTMLImageElement[]): Promise<unknown> {
    if (!images.length) return Promise.resolve();

    const decoded = Promise.all(
      images.map((img) => (typeof img.decode === 'function' ? img.decode().catch(() => undefined) : Promise.resolve())),
    );
    return Promise.race([decoded, this.delay(this.decodeTimeoutMs)]);
  }

  /**
   * `@page` cannot be scoped to an element, and styles.scss already pins A4 portrait for
   * the work order printout. Appending this last wins the cascade for the duration of
   * this print only, so the wide two-column scorecard gets landscape.
   */
  private addLandscapePageRule(): HTMLStyleElement {
    const style = document.createElement('style');
    style.dataset['vscPrint'] = '';
    style.textContent = '@media print { @page { size: A4 landscape; margin: 8mm; } }';
    document.head.appendChild(style);
    return style;
  }

  private weekToMonthPeriod(week: number, year: number): ScorecardPeriod {
    // Jan 4 is always in ISO week 1; advance by (week-1)*7 days to land inside the target week
    const jan4 = new Date(year, 0, 4);
    const dayInWeek = new Date(jan4.getTime() + (week - 1) * 7 * 24 * 60 * 60 * 1000);
    return parseMonthLabel(this.formatMonth(dayInWeek));
  }

  private formatMonth(d: Date): string {
    return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  }

  private isoWeek(d: Date): number {
    const jan1 = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d.getTime() - jan1.getTime()) / 86400000 + jan1.getDay() + 1) / 7);
  }

  // Last ISO week whose Monday falls within the last available month (monthOffset months back)
  private maxWeekForLastMonth(): number {
    const now = new Date();
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - this.monthOffset, 1);
    const lastDay = new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth() + 1, 0);
    const dow = lastDay.getDay(); // 0=Sun … 6=Sat
    const daysToMonday = dow === 0 ? 6 : dow - 1;
    const lastMonday = new Date(lastDay.getTime() - daysToMonday * 24 * 60 * 60 * 1000);
    return Math.max(1, this.isoWeek(lastMonday));
  }

  /** Months from the latest available month back to Jan 2025, newest first. */
  private buildMonthOptions(): string[] {
    const now = new Date();
    const latest = new Date(now.getFullYear(), now.getMonth() - this.monthOffset, 1);
    const count = (latest.getFullYear() - FIRST_MONTH.getFullYear()) * 12 + latest.getMonth() - FIRST_MONTH.getMonth() + 1;
    return Array.from({ length: Math.max(1, count) }, (_, i) => this.formatMonth(new Date(latest.getFullYear(), latest.getMonth() - i, 1)));
  }
}
